import { makeStyles } from '@material-ui/core/styles'
import { INTERACTIVE_CARD_HOVER_BOX_SHADOW } from 'lib/styles/interactive-card-hover'
import { pageContentHorizontalGutters } from 'lib/styles/page-content-gutters'
import { cardHeaderStyles } from 'lib/styles/card-header'
import { statTileStyles } from 'lib/styles/stat-tile'
import { DASHBOARD_BODY_TEXT } from 'pages/pga-coach/dashboard/dashboard-colors'

const CARD_BORDER = '#E0E0E0'
const CHART_HEIGHT = 280
const MESSAGE_MIN_HEIGHT = 160
const RANKED_ROW_HEIGHT = 75
const RANKED_LIST_VISIBLE_ROWS = 5

const useStyles = makeStyles(theme => ({
  container: pageContentHorizontalGutters(theme, {
    paddingBottom: theme.spacing(8),
  }),
  section: {
    marginTop: theme.spacing(4),
    '&:first-child': {
      marginTop: 0,
    },
  },
  sectionTitle: {
    marginBottom: theme.spacing(1.5),
    color: DASHBOARD_BODY_TEXT,
  },
  sectionNote: {
    margin: theme.spacing(1.5, 0, 0),
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  statGridFour: {
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  chartGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    alignItems: 'stretch',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  gridSpacing: {
    marginTop: theme.spacing(1.5),
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  card: {
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  ...cardHeaderStyles(theme),
  cardHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing(1.5),
  },
  cardSummary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
  cardSummaryColumn: {
    textAlign: 'right',
  },
  cardSummaryValue: {
    fontWeight: 700,
    fontSize: '1.5rem',
    lineHeight: 1.2,
    color: DASHBOARD_BODY_TEXT,
  },
  insightTip: {
    marginTop: theme.spacing(1.5),
    flexShrink: 0,
  },
  insightTipCopy: {
    alignItems: 'center',
  },
  insightTipIcon: {
    alignSelf: 'center',
    lineHeight: 1,
  },
  cardSubtitle: {
    fontSize: '12px',
    lineHeight: '18px',
    color: theme.palette.text.secondary,
  },
  cardBody: {
    padding: theme.spacing(2, 3),
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  cardBodyFlush: {
    padding: 0,
  },
  ...statTileStyles(theme),
  statHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  statCaption: {
    fontSize: '12px',
    lineHeight: '18px',
    color: theme.palette.text.secondary,
  },
  statSkeleton: {
    borderRadius: 4,
  },
  chartArea: {
    height: CHART_HEIGHT,
    minHeight: CHART_HEIGHT,
    width: '100%',
    flexShrink: 0,
  },
  chartMessage: {
    flex: '1 1 auto',
    minHeight: MESSAGE_MIN_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    fontSize: '12px',
    lineHeight: '18px',
    textAlign: 'center',
    padding: theme.spacing(0, 2),
  },
  messageError: {
    color: theme.palette.error.main,
  },
  loading: {
    margin: theme.spacing(3),
  },
  rankedList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    flex: '1 1 0',
    minHeight: RANKED_ROW_HEIGHT * RANKED_LIST_VISIBLE_ROWS,
    overflowY: 'auto',
  },
  rankedRow: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    minHeight: RANKED_ROW_HEIGHT,
    borderBottom: `1px solid ${CARD_BORDER}`,
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.02)',
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  rankedRowBody: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    flex: '1 1 auto',
    gap: theme.spacing(2),
    minWidth: 0,
    padding: theme.spacing(2, 3),
    color: 'inherit',
    textDecoration: 'none',
  },
  rankedRowBodyWithAction: {
    paddingRight: theme.spacing(1),
  },
  rankedRowLink: {
    cursor: 'pointer',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  rankedRowButton: {
    border: 0,
    margin: 0,
    backgroundColor: 'transparent',
    font: 'inherit',
    textAlign: 'left',
  },
  rankedAction: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    padding: theme.spacing(0, 3, 0, 0),
  },
  rankedActionButton: {
    padding: 6,
    width: 36,
    height: 36,
    borderRadius: 8,
    border: '1px solid #C4C7C9',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    transition: theme.transitions.create(
      ['background-color', 'border-color', 'color'],
      { duration: theme.transitions.duration.short },
    ),
    '&:hover': {
      backgroundColor: '#EBEBEB',
      borderColor: theme.palette.primary.main,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
    },
  },
  rankedAccentBar: {
    flex: '0 0 4px',
    alignSelf: 'stretch',
    borderRadius: 4,
  },
  rankedMain: {
    minWidth: 0,
    flex: '1 1 auto',
  },
  rankedName: {
    margin: 0,
    marginBottom: theme.spacing(0.5),
    fontWeight: 700,
    fontSize: '0.9375rem',
    lineHeight: 1.3,
    color: '#172024',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rankedDetail: {
    margin: 0,
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  rankedTrailing: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    marginLeft: 'auto',
    gap: theme.spacing(0.5),
  },
  rankedCaption: {
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  rankedChevron: {
    flex: '0 0 20px',
    width: 20,
    height: 20,
    fontSize: 20,
    color: 'rgba(0, 0, 0, 0.38)',
  },
  chartTooltip: {
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    boxShadow: INTERACTIVE_CARD_HOVER_BOX_SHADOW,
    padding: theme.spacing(1, 1.5),
    minWidth: 120,
  },
  chartTooltipLabel: {
    fontSize: '12px',
    lineHeight: '18px',
    fontWeight: 600,
    color: DASHBOARD_BODY_TEXT,
    marginBottom: theme.spacing(0.25),
  },
  chartTooltipValue: {
    fontSize: '12px',
    lineHeight: '18px',
    color: theme.palette.text.secondary,
  },
}))

export default useStyles
